import { ExternalLink, X } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import type { ReactNode } from "react";
import { Drawer as Vaul } from "vaul";
import { keepOpenOnToast } from "@/components/ui/overlays";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { usePhone } from "./use-media-query";

type PanelProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Sticky footer (the panel's actions: one primary at the end). */
  footer?: ReactNode;
  /** Extra header controls (a MoreMenu, a status chip). */
  headerActions?: ReactNode;
  /** Shows "Open full page" in the header. */
  onOpenFull?: () => void;
  /** 440 px (default) or 640 px on desktop. Phones always get a bottom drawer. */
  width?: "md" | "lg";
  className?: string;
  testId?: string;
};

/**
 * The side panel for details without losing your place: an end-side panel on desktop (slides in from the
 * left in Arabic), a native-feeling bottom drawer on phones (vaul: drag down to close).
 * Header (title, open full page, close) · scrolling body · sticky footer.
 */
export function SidePanel(props: PanelProps) {
  const phone = usePhone();
  return phone ? <PhoneDrawer {...props} /> : <DesktopPanel {...props} />;
}

function Header({ title, description, headerActions, onOpenFull, Title, Description, Close }: Pick<PanelProps, "title" | "description" | "headerActions" | "onOpenFull"> & { Title: typeof DialogPrimitive.Title; Description: typeof DialogPrimitive.Description; Close: typeof DialogPrimitive.Close }) {
  const { t } = useT();
  const iconBtn = "grid size-8 shrink-0 place-items-center rounded-lg text-ink-3 hover:bg-surface-2 hover:text-ink";
  return (
    <header className="flex items-start gap-2 border-b border-line px-5 pt-4 pb-3.5">
      <div className="min-w-0 flex-1">
        <Title className="truncate text-[16px] font-semibold text-ink">{title}</Title>
        {description ? <Description className="mt-0.5 text-[13px] text-ink-3">{description}</Description> : <Description className="sr-only">{title}</Description>}
      </div>
      {headerActions}
      {onOpenFull && (
        <button type="button" className={iconBtn} onClick={onOpenFull} aria-label={t("kit.openFullPage")} title={t("kit.openFullPage")} data-testid="panel-open-full">
          <ExternalLink className="size-4 flip-rtl" aria-hidden />
        </button>
      )}
      <Close className={iconBtn} aria-label={t("kit.close")} title={t("kit.close")}>
        <X className="size-4" aria-hidden />
      </Close>
    </header>
  );
}

function DesktopPanel({ open, onOpenChange, children, footer, width = "md", className, testId, ...head }: PanelProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-[var(--overlay)] data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content
          onInteractOutside={keepOpenOnToast}
          data-testid={testId ?? "side-panel"}
          className={cn(
            "fixed inset-y-0 end-0 z-50 flex w-full flex-col border-s border-line bg-surface shadow-overlay outline-none data-[state=open]:animate-[sheet-end_.32s_cubic-bezier(.22,1,.36,1)]",
            width === "lg" ? "max-w-[640px]" : "max-w-[440px]",
            className,
          )}
        >
          <Header {...head} Title={DialogPrimitive.Title} Description={DialogPrimitive.Description} Close={DialogPrimitive.Close} />
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="flex items-center justify-end gap-2 border-t border-line bg-surface px-5 py-3">{footer}</footer>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function PhoneDrawer({ open, onOpenChange, children, footer, className, testId, ...head }: PanelProps) {
  return (
    <Vaul.Root open={open} onOpenChange={onOpenChange}>
      <Vaul.Portal>
        <Vaul.Overlay className="fixed inset-0 z-50 bg-[var(--overlay)]" />
        <Vaul.Content data-testid={testId ?? "side-panel"} className={cn("fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] flex-col rounded-t-3xl border-t border-line bg-surface shadow-overlay outline-none", className)}>
          <Vaul.Handle className="mx-auto mt-2.5 mb-1 h-1.5 w-10 rounded-full bg-line-2" />
          <Header {...head} Title={Vaul.Title as typeof DialogPrimitive.Title} Description={Vaul.Description as typeof DialogPrimitive.Description} Close={Vaul.Close as typeof DialogPrimitive.Close} />
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer && <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]">{footer}</footer>}
        </Vaul.Content>
      </Vaul.Portal>
    </Vaul.Root>
  );
}

/**
 * A bottom drawer on every screen size (phone "More" sheet, mobile filters). `title` is required for screen readers;
 * set `hideTitle` to keep it visually hidden.
 */
export function Drawer({ open, onOpenChange, title, hideTitle, children, className, testId }: { open: boolean; onOpenChange: (o: boolean) => void; title: ReactNode; hideTitle?: boolean; children: ReactNode; className?: string; testId?: string }) {
  return (
    <Vaul.Root open={open} onOpenChange={onOpenChange}>
      <Vaul.Portal>
        <Vaul.Overlay className="fixed inset-0 z-50 bg-[var(--overlay)]" />
        <Vaul.Content data-testid={testId} className={cn("fixed inset-x-0 bottom-0 z-50 flex max-h-[90dvh] flex-col rounded-t-3xl border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] shadow-overlay outline-none", className)}>
          <Vaul.Handle className="mx-auto mt-2.5 mb-2 h-1.5 w-10 rounded-full bg-line-2" />
          <Vaul.Title className={cn("px-5 pb-2 text-[15px] font-semibold text-ink", hideTitle && "sr-only")}>{title}</Vaul.Title>
          <Vaul.Description className="sr-only">{title}</Vaul.Description>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">{children}</div>
        </Vaul.Content>
      </Vaul.Portal>
    </Vaul.Root>
  );
}
